import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Megaphone, Loader2, MousePointerClick, Eye, Trophy, Pause, Play } from "lucide-react";
import { startEnchereCampaign, fetchEnchereReport, toggleEncherePause, verifyProSubscription } from "@/lib/api";
import { fmtPrice } from "@/lib/utils";

const Tile = ({ label, value, testid }) => (
  <div className="rounded-xl bg-white/70 border border-[var(--line)] px-3 py-2" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-[var(--ink-soft)]">{label}</div>
    <div className="font-bold text-sm">{value}</div>
  </div>
);

export const EnchereVisibiliteCard = ({ hotel }) => {
  const [params] = useSearchParams();
  const [report, setReport] = useState(null);
  const [bid, setBid] = useState(200);
  const [budget, setBudget] = useState(5000);
  const [phone, setPhone] = useState(hotel?.manager_phone || "");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = () => fetchEnchereReport(hotel.id).then(setReport).catch(() => setReport(null));

  useEffect(() => {
    if (!hotel?.id) return;
    const tx = params.get("tx");
    if (params.get("paiement") && tx && tx.startsWith("ENC-")) {
      setBusy(true);
      verifyProSubscription(tx).catch(() => {}).finally(() => { setBusy(false); load(); });
    } else load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotel?.id]);

  const clicks = bid > 0 ? Math.floor(budget / bid) : 0;
  const cur = report?.current;

  const start = async () => {
    setBusy(true); setMsg("");
    try {
      const r = await startEnchereCampaign(hotel.id, { bid_per_click: Number(bid), budget_total: Number(budget), phone });
      if (r.payment_url.startsWith("http")) window.location.href = r.payment_url;
      else window.location.assign(r.payment_url);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setMsg(typeof d === "string" ? d : d?.[0]?.msg || "Erreur de paiement");
      setBusy(false);
    }
  };

  const pause = async () => {
    setBusy(true);
    try { await toggleEncherePause(hotel.id); await load(); } catch (e) { setMsg(e?.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
  };

  return (
    <div className="glass-card p-5 border-2 border-violet-200" data-testid="enchere-card">
      <div className="flex items-center gap-2 text-sm font-bold mb-1"><Megaphone size={16} className="text-violet-600" /> Enchere de visibilite — payez au clic, apparaissez en premier</div>
      <p className="text-xs text-[var(--ink-soft)] mb-4">Comme Trivago/Kayak : vous prepayez un budget et fixez votre enchere par clic. Votre hotel remonte en tete des listes tant qu'il reste du budget. Facture uniquement au clic reel.</p>

      {cur && (
        <div className={`mb-4 rounded-2xl p-4 ${cur.status === "active" ? "bg-violet-50 border border-violet-200" : "bg-amber-50 border border-amber-200"}`} data-testid="enchere-current">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-sm font-bold" data-testid="enchere-status">
              {cur.status === "active" ? "Campagne active" : cur.status === "paused" ? "Campagne en pause" : "Budget epuise — rechargez"}
              {report.rank && cur.status === "active" && <span className="ml-2 inline-flex items-center gap-1 text-violet-700"><Trophy size={13} /> Position n°{report.rank} / {report.competitors}</span>}
            </div>
            {(cur.status === "active" || cur.status === "paused") && (
              <button type="button" onClick={pause} disabled={busy} className="text-xs font-semibold inline-flex items-center gap-1 underline" data-testid="enchere-pause-btn">
                {cur.status === "active" ? <><Pause size={12} /> Mettre en pause</> : <><Play size={12} /> Reprendre</>}
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
            <Tile label="Affichages" value={cur.impressions} testid="enchere-impressions" />
            <Tile label="Clics" value={`${cur.clicks} (${cur.ctr_pct}%)`} testid="enchere-clicks" />
            <Tile label="Enchere / clic" value={fmtPrice(cur.bid_per_click)} testid="enchere-bid" />
            <Tile label="Budget restant" value={`${fmtPrice(cur.budget_remaining)} · ${cur.clicks_left} clics`} testid="enchere-remaining" />
          </div>
          <div className="h-2 mt-3 rounded-full bg-white overflow-hidden">
            <div className="h-full bg-gradient-to-r from-violet-500 to-fuchsia-500 transition-[width] duration-700" style={{ width: `${cur.budget_total ? Math.round((cur.budget_remaining / cur.budget_total) * 100) : 0}%` }} />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
        <div>
          <label>Enchere par clic (FCFA)</label>
          <input type="number" min={report?.min_bid || 100} step="5" value={bid} onChange={(e) => setBid(e.target.value)} data-testid="enchere-bid-input" />
        </div>
        <div>
          <label>Budget a prepayer (FCFA)</label>
          <input type="number" min={report?.min_budget || 1000} step="5" value={budget} onChange={(e) => setBudget(e.target.value)} data-testid="enchere-budget-input" />
        </div>
        <div>
          <label>Numero Mobile Money</label>
          <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+225 07 00 00 00 00" data-testid="enchere-phone-input" />
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm inline-flex items-center gap-2 text-[var(--ink-soft)]" data-testid="enchere-estimate">
          <MousePointerClick size={14} className="text-violet-600" /> Ce budget couvre environ <b className="text-[var(--ink)]">{clicks} clics</b> vers votre fiche
          <Eye size={14} className="ml-2 text-violet-600" /> affichages illimites et gratuits
        </div>
        <button type="button" onClick={start} disabled={busy || clicks < 1} className="btn-primary inline-flex items-center gap-2 !bg-violet-600" data-testid="enchere-start-btn">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Megaphone size={14} />} {cur ? "Recharger / modifier l'enchere" : "Lancer la campagne"} — {fmtPrice(budget)}
        </button>
      </div>
      {msg && <div className="mt-3 text-sm font-semibold text-red-600" data-testid="enchere-msg">{msg}</div>}
      <p className="mt-3 text-[11px] text-[var(--ink-soft)]">Alertes SMS automatiques quand il reste 3 clics ou quand le budget est epuise. Bilan SMS chaque lundi 8h.</p>
    </div>
  );
};
